import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Modal, Typography } from "@mui/material"
import { CloseOutlined } from "@mui-symbols-material/w300"
import type { FunctionComponent } from "react"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import type { Company, CompanyCreateFailure, CompanyCreateInput, CompanyCreateSuccess } from "@/graphql/codegen/graphql"
import { CompanyPreferredMeasurementSystemEnum, CompanyStatus } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { CompanyDuplicateConfirmationDialog } from "@/screens/Companies/components/CompanyDuplicateConfirmationDialog"
import CreateNewCompanyForm from "@/screens/Companies/create/components/CreateNewCompanyForm"
import { CompanyModalSchema } from "@/screens/Companies/create/components/CreateNewCompanySchema"
import { useCreateNewCompanyModal } from "@/screens/Companies/hooks/useCreateNewCompanyModal"
import { toCompanyFormErrorField } from "@/screens/Companies/utils/company"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { DialogCloseAction } from "@/types/common"
import type { TreeNode } from "@/types/tree"

const NewCompanySuccessMessage = memo(() => (
  <>
    <p>A new company has been successfully created.</p>
    <p>Add required information to complete company's setup or save and come back later.</p>
  </>
))

NewCompanySuccessMessage.displayName = "NewCompanySuccessMessage"

interface CreateNewCompanyModalContainerProps {
  isOpen: boolean
  onClose: (data?: CompanyCreateInput, reason?: DialogCloseAction) => void
  onSuccess?: (value: ((prevState: boolean) => boolean) | boolean | Company) => void
}

const defaultFormValues: CompanyCreateInput = {
  name: "",
  description: "",
  types: [],
  status: CompanyStatus.Active,
  preferredMeasurementSystem: CompanyPreferredMeasurementSystemEnum.UsStandard,
  defaultUom: undefined,
}

const CreateNewCompanyModal: FunctionComponent<CreateNewCompanyModalContainerProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)
  const [isDuplicateConfirmationOpen, setIsDuplicateConfirmationOpen] = useState(false)
  const [pendingCreateData, setPendingCreateData] = useState<CompanyCreateInput | null>(null)
  const [step, setStep] = useState(1)

  const { companyTypeList, createCompany, checkCompanyNameDuplicate } = useCreateNewCompanyModal()
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const methods = useForm<CompanyCreateInput>({
    defaultValues: defaultFormValues,
    resolver: zodResolver(CompanyModalSchema),
    mode: "onChange",
  })

  const { setError, clearErrors, handleSubmit, reset } = methods

  const handleNextStep = useCallback(() => {
    setStep((prevStep) => prevStep + 1)
  }, [])

  const handleCancel = useCallback(() => {
    setIsCancelConfirmationOpen(true)
  }, [])

  const handleModalClose = useCallback(() => {
    reset(defaultFormValues)
    clearErrors()
    setStep(1)
    setPendingCreateData(null)
    setIsDuplicateConfirmationOpen(false)
    onClose()
  }, [onClose, reset, clearErrors])

  const confirmCancel = useCallback(() => {
    setIsCancelConfirmationOpen(false)
    handleModalClose()
  }, [handleModalClose])

  useEffect(() => {
    if (!isOpen) {
      setStep(1)
      setIsCancelConfirmationOpen(false)
      setIsDuplicateConfirmationOpen(false)
      setPendingCreateData(null)
      reset(defaultFormValues)
      clearErrors()
    }
  }, [isOpen, reset, clearErrors])

  const handleCompanyCreate = useCallback(
    async (data: CompanyCreateInput) => {
      const { error, data: company } = await createCompany({
        input: { ...data, description: data.description?.trim() || undefined, status: CompanyStatus.Active },
      })

      const hasError =
        error?.message ||
        (company?.company.create.__typename === "CompanyCreateFailure" && Boolean(company?.company.create.error))

      if (hasError) {
        const failure =
          company?.company.create.__typename === "CompanyCreateFailure" ? company.company.create : undefined
        setError(toCompanyFormErrorField(failure?.error.field), {
          type: "server",
          message: error?.message ?? (company?.company.create as CompanyCreateFailure).error?.message,
        })
        notify({
          type: NotificationType.Error,
          silent: false,
          message: error?.message ?? "Failed to create company",
        })
        return
      }

      if (onSuccess) {
        onSuccess((company?.company.create as CompanyCreateSuccess).company)
        notify({
          type: NotificationType.Success,
          silent: false,
          content: <NewCompanySuccessMessage />,
        })
        handleModalClose()
      }
    },
    [createCompany, notify, onSuccess, setError, handleModalClose]
  )

  const handleDuplicateConfirm = useCallback(async () => {
    setIsDuplicateConfirmationOpen(false)
    if (pendingCreateData) {
      await handleCompanyCreate(pendingCreateData)
      setPendingCreateData(null)
    }
  }, [pendingCreateData, handleCompanyCreate])

  const handleDuplicateCancel = useCallback(() => {
    setIsDuplicateConfirmationOpen(false)
    setPendingCreateData(null)
  }, [])

  const handleFormSubmit = useCallback(
    async (data: CompanyCreateInput) => {
      if (await checkCompanyNameDuplicate(data.name)) {
        setPendingCreateData(data)
        setIsDuplicateConfirmationOpen(true)
        return
      }

      await handleCompanyCreate(data)
    },
    [checkCompanyNameDuplicate, handleCompanyCreate]
  )

  const onSubmit = handleSubmit(handleFormSubmit)

  const modalStyles = useMemo(
    () => ({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      bgcolor: "white",
      boxShadow: 24,
      width: 576,
      borderRadius: 2,
    }),
    []
  )

  return (
    <>
      {isDuplicateConfirmationOpen && (
        <CompanyDuplicateConfirmationDialog
          confirmLabel='Create Anyway'
          onClose={handleDuplicateCancel}
          onConfirm={handleDuplicateConfirm}
        />
      )}
      <Modal
        open={isOpen}
        onClose={handleCancel}
        disableEscapeKeyDown
        onKeyDown={(e) => {
          const target = e.target as HTMLElement
          if (e.key === "Enter" && !isCancelConfirmationOpen && target.tagName !== "BUTTON") {
            e.preventDefault()
          }
        }}
      >
        <Box sx={modalStyles}>
          {isCancelConfirmationOpen ? (
            <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={confirmCancel} />
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, pl: 5 }}>
                <Typography variant='h6' color='primary'>
                  New Company
                </Typography>
                <Button size='small' appearance='text' onClick={handleCancel}>
                  <CloseOutlined />
                </Button>
              </Box>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
              <FormProvider {...methods}>
                <CreateNewCompanyForm
                  companyTypeList={companyTypeList as TreeNode<string>[]}
                  step={step}
                  handleCancel={handleCancel}
                  handleNextStep={handleNextStep}
                  onSubmit={onSubmit}
                />
              </FormProvider>
            </>
          )}
        </Box>
      </Modal>
    </>
  )
}

export default memo(CreateNewCompanyModal)
